// In-memory mock backend (config.useMock=true) emulating the REAL server contract
// (internal/httpapi/router.go). Paths + response envelopes match production so the client
// adapters (client.ts) see exactly what the live API would emit. Passkey ceremonies are
// simulated; DPoP is bypassed. DEV/TEST convenience only — not security.
//
// Server envelope conventions emulated here:
//   GET /fields        -> {fields:[...]}
//   GET /field-types   -> {field_types:[...]}
//   GET /profiles      -> {profiles:[...]}
//   GET /me/passkeys   -> {passkeys:[...]}
//   GET /me/totp       -> {authenticators:[...]}
//   GET /share-links   -> {share_links:[...]}
//   GET /contacts      -> {contacts:[...]}  (each contact carries notes:[{id,body,...}])
//   GET /subscribers   -> {subscribers:[...]}
//   POST /auth/*/begin -> {ceremony_id, options}
//   POST /auth/*/finish, /auth/token/renew, /auth/join/finish -> auth-success envelope

import type { RequestOpts } from './http.js';
import { session } from './session.js';
import { handle2 } from './mock2.js';
import type {
  Identity, FieldInstance, Profile, ShareLink, Subscription, AuthMethod, FieldType,
  Contact, AuthSuccess, PublicProfile, Note, EmailState,
} from './types.js';

const now = () => new Date().toISOString();
let seq = 100;
const id = (p: string) => `${p}_${++seq}`;

export interface DB {
  identity: Identity;
  fields: FieldInstance[];
  profiles: Profile[];
  exposures: Record<string, Record<string, boolean>>; // profileId -> fieldId -> exposed
  links: ShareLink[];
  subs: Subscription[];      // owner-side subscribers
  contacts: Contact[];       // subscriber-side contacts (carry notes[] + labels[])
  passkeys: AuthMethod[];
  totp: AuthMethod[];
  fieldTypes: FieldType[];
  publicProfile: PublicProfile;
  email: EmailState & { code?: string; attempts?: number };
}

function seed(): DB {
  const ident: Identity = {
    schema_version: 1, id: 'idn_alex', type: 'individual', display_name: 'Alex Kim', handle: 'alex-kim', username: 'alex-kim', auto_accept_contacts: true,
    status: 'active', region: 'eu', subscriber_count: 128, created_at: '2026-03-02T10:00:00Z', updated_at: now(),
  };
  // Server field wire shape uses `type` (not field_type_key).
  const F = (type: string, label: string, value: string): FieldInstance =>
    ({ id: id('fld'), type, label, value, created_at: '2026-03-02T10:00:00Z', updated_at: now() });
  const fields = [
    F('full_name', 'Full name', 'Alex Kim'),
    F('email', 'work', 'alex@northwind.studio'),
    F('email', 'Email', 'alex.kim@gmail.com'),
    F('phone', 'work', '+49 30 5550 7000'),
    F('phone', 'mobile', '+49 151 5550 4821'),
    F('postal_address', 'work', 'Torstraße 12, Berlin'),
    F('company', 'Company', 'Northwind Studio'),
    F('job_title', 'Job title', 'Principal Designer'),
    F('url', '', 'alexkim.design'),
    F('linkedin', '', 'linkedin.com/in/alexkim'),
    F('instagram', '', '@alex.kim.design'),
    F('whatsapp', '', '+49 151 5550 4821'),
    F('telegram', '', '@alexk'),
    F('spotify', '', 'alexkim'),
    F('threads', '', '@alex.kim.design'),
    F('twitch', '', 'alexkimlive'),
    F('kakaotalk', '', 'alexk_studio'),
    F('avis_wizard', '', 'A1234567'),
    F('hertz_gold', '', 'HZ99887766'),
    F('date_of_birth', '', '1990/05/19'),
    F('national_id', 'National ID / SSN', '•••• 4821'),
    F('department', 'Department', 'Design'),
  ];
  // Server profile wire shape has no exposed_field_count/subscriber_count.
  const P = (name: string, is_public: boolean, desc: string, slug?: string): Profile =>
    ({ id: id('prf'), name, is_public, slug, description: desc, icon: '', color: '', avatar_url: null, created_at: '2026-03-02T10:00:00Z', updated_at: now() });
  const profiles = [
    P('Work', false, 'Name, title, work email & phone, company.', 'work'),
    P('Personal', false, 'Personal email, mobile, socials — for friends & family.'),
    P('Medical', false, 'Emergency contact, blood type, allergies, insurer.'),
    P('Public', true, 'What anyone sees at virtualid.one/id/alex-kim.', 'public'),
    P('Friends', false, 'Fields you share with friends.', 'friends'),
    P('Family', false, 'Fields you share with family.', 'family'),
  ];
  const exposures: DB['exposures'] = {};
  const exposedCounts = [7, 5, 6, 4, 6, 8];
  profiles.forEach((p, pi) => {
    exposures[p.id] = {};
    fields.forEach((f, i) => { exposures[p.id][f.id] = i < exposedCounts[pi]; });
  });
  // Explicitly expose the Department field on the Work profile (it lives at the
  // end of the fields array, outside the index-based exposedCounts window). Work
  // is profiles[0]. Mirrors the API's provisioning step v3.
  {
    const deptField = fields.find(f => f.type === 'department');
    if (deptField) exposures[profiles[0].id][deptField.id] = true;
  }
  const workId = profiles[0].id, personalId = profiles[1].id, publicId = profiles[3].id, medId = profiles[2].id;

  // Server share-link wire shape: {id, profile_id, token, label, max_uses, use_count, expires_at, revoked_at, created_at}.
  const L = (label: string, profile_id: string, expires_at: string | null, use_count: number): ShareLink =>
    ({ id: id('lnk'), profile_id, token: id('tok'), label, max_uses: null, use_count, expires_at, revoked_at: null, created_at: now() });
  const links = [
    L('Expo booth QR', workId, '2026-07-26T16:30:00Z', 212),
    L('Recruiter intro link', workId, '2026-08-29T07:00:00Z', 4),
    L('Email signature', workId, '2027-01-31T22:59:59Z', 1043),
    L('Business cards Q3 — Acme Print, 500 units', workId, null, 76),
    L('NFC tag — front desk', publicId, null, 318),
  ];
  // Server subscribers wire shape: {id, subscriber_id, profile_id, status, added_at}. The
  // richer subscriber{} display block + labels are mock extras the adapter tolerates.
  const S = (name: string, company: string, profile_id: string, status: Subscription['status'], labels: string[], since: string): Subscription =>
    ({ id: id('sub'), subscriber_id: id('idn'), profile_id, status, added_at: since,
       subscriber: { kind: 'identity', type: 'individual', display_name: name, company }, labels, since });
  const subs = [
    S('Priya Nair', 'Meridian Labs', workId, 'active', ['client', 'vip'], '2026-07-14'),
    S('Marco Ruiz', 'Ruiz & Co', publicId, 'active', ['event'], '2026-07-11'),
    S('Dr. Lena Hoff', 'Charité', medId, 'active', ['doctor'], '2026-06-30'),
    S('Sam Okoye', 'Freelance', personalId, 'active', ['friend'], '2026-06-22'),
    S('Nadia Farooq', 'Talentworks', workId, 'pending', ['recruiter'], '2026-06-18'),
    S('Tom Becker', 'Northwind', workId, 'active', ['colleague', 'vip'], '2026-05-09'),
  ];
  // Contacts carry server-style notes[] + sync_state. display_name/company are mock display extras.
  const N = (body: string): Note[] => body ? [{ id: id('note'), body, created_at: now(), updated_at: now() }] : [];
  const C = (name: string, company: string, labels: string[], note: string, live: boolean): Contact =>
    ({ id: id('sub'), subscription_id: '', owner_id: 'idn_alex', profile_id: 'prf_ext', display_name: name, company,
       status: live ? 'active' : 'stale', sync_state: live ? 'live' : 'stale', labels, notes: N(note),
       added_at: '2026-06-01', last_synced_at: live ? now() : '2026-06-20',
       fields: [
         { key: 'fld_email', type: 'email', label: 'Email', value: `${name.split(' ')[0].toLowerCase()}@example.com`, syncing: true },
         { key: 'fld_phone', type: 'phone', label: 'Phone', value: '+49 30 5550 1000', syncing: true },
       ] });
  const contacts = [
    C('Priya Nair', 'Meridian Labs', ['client', 'vip', 'berlin'], 'Met at Kraków conf 2025. Decision-maker on the Meridian deal. Prefers Signal over email. Follow up re: Q4 renewal.', true),
    C('Marco Ruiz', 'Ruiz & Co', ['event'], 'Scanned my expo QR. Interested in partnership — send deck.', true),
    C('Dr. Lena Hoff', 'Charité', ['doctor'], '', true),
    C('Sam Okoye', 'Freelance', ['friend'], '', true),
    C('Nadia Farooq', 'Talentworks', ['recruiter'], 'Recruiter — polite decline for now.', false),
  ];
  const passkeys: AuthMethod[] = [
    { id: id('pk'), kind: 'passkey', label: 'MacBook Pro', credential_id: 'cred_mbp', sign_count: 12, transports: ['internal'], created_at: '2026-03-02', last_used_at: now() },
    { id: id('pk'), kind: 'passkey', label: 'iPhone 15', credential_id: 'cred_iph', sign_count: 40, transports: ['internal', 'hybrid'], created_at: '2026-03-02', last_used_at: '2026-07-13' },
    { id: id('pk'), kind: 'passkey', label: 'YubiKey 5C', credential_id: 'cred_yk', sign_count: 5, transports: ['usb', 'nfc'], created_at: '2026-04-18', last_used_at: '2026-06-30' },
  ];
  const totp: AuthMethod[] = [
    { id: id('totp'), kind: 'totp', label: 'Authenticator app', confirmed: true, created_at: '2026-04-18' },
  ];
  // Minimal field-type catalog for mock/dev mode so views resolve friendly type
  // names (typeDisplayName) instead of falling back to KEY.toUpperCase(). Mirrors
  // the keys/labels of the compiled-in Go catalog (internal/fieldcatalog) for the
  // subset used in this demo. The real app fetches the full catalog from the API.
  const ft = (key: string, label: string, category: string): FieldType => ({ key, label, category, multi_value: true });
  const fieldTypes: FieldType[] = [
    ft('full_name', 'Full name', 'name'),
    ft('email', 'Email', 'contact'),
    ft('phone', 'Phone', 'contact'),
    ft('postal_address', 'Postal address', 'address'),
    ft('company', 'Company', 'contact'),
    ft('job_title', 'Job title', 'contact'),
    ft('department', 'Department', 'contact'),
    ft('url', 'Website', 'contact'),
    ft('date_of_birth', 'Date of birth', 'date'),
    ft('national_id', 'National ID / SSN', 'identifier'),
    ft('linkedin', 'LinkedIn', 'social'),
    ft('instagram', 'Instagram', 'social'),
    ft('spotify', 'Spotify', 'social'),
    ft('threads', 'Threads', 'social'),
    ft('twitch', 'Twitch', 'social'),
    ft('whatsapp', 'WhatsApp', 'messaging'),
    ft('telegram', 'Telegram', 'messaging'),
    ft('kakaotalk', 'KakaoTalk', 'messaging'),
    ft('avis_wizard', 'Avis Wizard Number', 'loyalty'),
    ft('hertz_gold', 'Hertz Gold Plus Rewards', 'loyalty'),
  ];
  return {
    identity: ident, fields, profiles, exposures, links, subs, contacts, passkeys, totp, fieldTypes,
    publicProfile: { enabled: true, alias: 'alex-kim', profile_id: publicId },
    email: { email: '', verified: false, pending: false, resend_available_at: null },
  };
}

const authSuccess = (): AuthSuccess => {
  const t: AuthSuccess = { token: 'mock.access.' + id('t'), access_token: 'mock.access.' + id('t'),
    refresh_token: 'mock.refresh.' + id('t'), expires_in: 900, token_type: 'DPoP', user_id: 'idn_alex' };
  session.set(t);
  return t;
};

export class MockBackend {
  db = seed();

  async handle<T>(path: string, opts: RequestOpts): Promise<T> {
    await new Promise((r) => setTimeout(r, 5)); // simulate latency
    const method = (opts.method || 'GET').toUpperCase();
    const [p] = path.split('?');
    const body = (opts.body || {}) as any;
    const m = (re: RegExp) => p.match(re);
    const r = (v: unknown) => v as T;

    // ---- Auth: passkey signup / login ceremonies ----
    if (p === '/auth/username/check') {
      const u = (body.username || '').toLowerCase();
      const taken = ['admin', 'alex', 'root'].includes(u);
      return r(taken ? { available: false, reason: 'This username is taken.' } : u.length < 3 ? { available: false, reason: 'Too short (min 3 chars).' } : { available: true });
    }
    if (p === '/auth/register/begin' || p === '/auth/login/begin') {
      const isReg = p.includes('register');
      const options = { publicKey: { challenge: 'bW9jay1jaGFsbGVuZ2U', rpId: 'virtualid.one', timeout: 60000, userVerification: 'preferred',
        ...(isReg
          ? { user: { id: 'bW9jay11c2Vy', name: body.username || 'alex', displayName: body.display_name || body.username || 'Alex' }, pubKeyCredParams: [{ type: 'public-key', alg: -7 }], rp: { id: 'virtualid.one', name: 'VirtualID' } }
          : { allowCredentials: [] }) } };
      return r({ ceremony_id: id('cer'), options });
    }
    if (p === '/auth/register/finish' || p === '/auth/login/finish') return r(authSuccess());
    if (p === '/auth/recover') return r(authSuccess());
    if (p === '/auth/token/renew') return r(authSuccess());
    if (p === '/auth/logout') { session.clear(); return r({ status: 'logged_out' }); }

    // ---- Cross-device join ----
    if (p === '/auth/join/issue') return r({ join_id: id('join'), code: '482913', expires_in: 120 });
    if (p === '/auth/join/redeem') return r({ join_id: id('join'), status: 'pending', message: 'Approve on a trusted device.' });
    if (p === '/auth/join/register/begin') return r({ ceremony_id: id('cer'), options: { publicKey: {} }, existing_passkeys: 0 });
    if (p === '/auth/join/approve') return r({ join_id: body.join_id || id('join'), status: 'approved' });
    if (p === '/auth/join/deny') return r({ join_id: body.join_id || id('join'), status: 'denied' });
    if (p === '/auth/join/pending') return r({ requests: [] });
    if (p === '/auth/join/finish') return r(authSuccess());

    // ---- Passkeys (/me/passkeys) ----
    if (p === '/me/passkeys' && method === 'GET') return r({ passkeys: this.db.passkeys });
    if (m(/^\/me\/passkeys\/(.+)/) && method === 'DELETE') {
      const pid = m(/^\/me\/passkeys\/(.+)/)![1];
      if (this.db.passkeys.length + this.db.totp.length <= 1) throw mkErr(409, 'last_method', 'Cannot remove the last auth method.');
      this.db.passkeys = this.db.passkeys.filter((x) => x.id !== pid); return r(undefined);
    }

    // ---- TOTP (/me/totp) ----
    if (p === '/me/totp' && method === 'GET') return r({ authenticators: this.db.totp });
    if (p === '/me/totp/begin') {
      const t: AuthMethod = { id: id('totp'), kind: 'totp', label: body.label || 'Authenticator app', confirmed: false, created_at: now() };
      this.db.totp.push(t);
      return r({ id: t.id, secret: 'JBSWY3DPEHPK3PXP', otpauth_url: 'otpauth://totp/VirtualID:alex?secret=JBSWY3DPEHPK3PXP&issuer=VirtualID' });
    }
    if (p === '/me/totp/finish') {
      const t = this.db.totp.find((x) => x.id === body.id); if (t) t.confirmed = true;
      return r({ id: body.id, confirmed: true });
    }
    if (m(/^\/me\/totp\/(.+)/) && method === 'DELETE') {
      const tid = m(/^\/me\/totp\/(.+)/)![1]; this.db.totp = this.db.totp.filter((x) => x.id !== tid); return r(undefined);
    }
    if (p === '/me/recovery-key') return r({ recovery_key: 'vid-recover-MOCK-word-word-word-word-word-word', note: 'Store this somewhere safe.' });

    // ---- Primary email + verification (/me/email) ----
    // Public state view: never leaks the code/attempts internals.
    if (m(/^\/me\/email$/)) {
      const e = this.db.email;
      const view = (): EmailState =>
        ({ email: e.email, verified: e.verified, pending: e.pending, resend_available_at: e.resend_available_at });
      if (method === 'GET') return r(view());
      if (method === 'POST') {
        const email = String(body.email || '').trim().toLowerCase();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw mkErr(400, 'EMAIL_INVALID', 'That email address looks invalid.');
        if (email === 'taken@virtualid.one') throw mkErr(409, 'EMAIL_TAKEN', 'That email is already verified on another account.');
        e.email = email; e.verified = false; e.pending = true;
        e.code = '123456'; e.attempts = 0;
        e.resend_available_at = new Date(Date.now() + 60_000).toISOString();
        return r(view());
      }
      if (method === 'DELETE') {
        if (!e.email) throw mkErr(404, 'not_found', 'No email to remove.');
        e.email = ''; e.verified = false; e.pending = false; e.resend_available_at = null;
        e.code = undefined; e.attempts = 0;
        return r(undefined);
      }
    }
    if (p === '/me/email/verify' && method === 'POST') {
      const e = this.db.email;
      if (!e.email) throw mkErr(404, 'not_found', 'No email to verify.');
      if ((e.attempts ?? 0) >= 5) throw mkErr(429, 'CODE_LOCKED', 'Too many attempts.');
      const code = String(body.code || '').trim();
      if (code === '000000') throw mkErr(422, 'CODE_EXPIRED', 'That code has expired.');
      if (code !== (e.code || '')) { e.attempts = (e.attempts ?? 0) + 1; throw mkErr(422, 'CODE_INVALID', 'Incorrect code.'); }
      e.verified = true; e.pending = false; e.resend_available_at = null; e.code = undefined; e.attempts = 0;
      return r({ email: e.email, verified: true, pending: false, resend_available_at: null });
    }
    if (p === '/me/email/resend' && method === 'POST') {
      const e = this.db.email;
      if (!e.email) throw mkErr(404, 'not_found', 'No email to resend to.');
      if (e.verified) throw mkErr(409, 'already_verified', 'Email already verified.');
      if (e.resend_available_at && new Date(e.resend_available_at).getTime() > Date.now())
        throw mkErr(429, 'RESEND_TOO_SOON', 'Please wait before resending.');
      e.code = '123456'; e.attempts = 0;
      e.resend_available_at = new Date(Date.now() + 60_000).toISOString();
      return r({ email: e.email, verified: false, pending: true, resend_available_at: e.resend_available_at });
    }

    // ---- Identity & fields ----
    if (p === '/me' && method === 'GET') return r(this.db.identity);
    if (p === '/me' && method === 'PATCH') {
      if (body && typeof body.display_name === 'string') {
        const name = body.display_name.trim();
        if (!name) throw mkErr(400, 'validation_failed', 'display_name cannot be empty');
        (this.db.identity as any).display_name = name;
      }
      if (body && typeof body.auto_accept_contacts === 'boolean') {
        (this.db.identity as any).auto_accept_contacts = body.auto_accept_contacts;
      }
      return r(this.db.identity);
    }
    if (p === '/me' && method === 'DELETE') {
      if (!body || body.confirm_username !== this.db.identity.username) {
        throw mkErr(403, 'confirmation_mismatch', 'confirm_username does not match your account');
      }
      const credential_ids = this.db.passkeys.map((k) => k.credential_id).filter(Boolean) as string[];
      return r({ deleted: true, credential_ids });
    }
    if (p === '/field-types' && method === 'GET') return r({ field_types: this.db.fieldTypes });
    if (p === '/fields' && method === 'GET') return r({ fields: this.db.fields });
    if (p === '/fields' && method === 'POST') {
      const f: FieldInstance = { id: id('fld'), type: body.type, label: body.label, value: body.value, created_at: now(), updated_at: now() };
      this.db.fields.push(f); return r(f);
    }
    if (m(/^\/fields\/(.+)/) && method === 'PATCH') {
      const fid = m(/^\/fields\/(.+)/)![1]; const f = this.db.fields.find((x) => x.id === fid);
      if (!f) throw mkErr(404, 'not_found', 'Field not found.');
      if (body.type !== undefined) f.type = body.type;
      if (body.label !== undefined) f.label = body.label;
      if (body.value !== undefined) f.value = body.value;
      f.updated_at = now(); return r(f);
    }
    if (m(/^\/fields\/(.+)/) && method === 'DELETE') {
      const fid = m(/^\/fields\/(.+)/)![1]; this.db.fields = this.db.fields.filter((x) => x.id !== fid); return r(undefined);
    }

    return handle2<T>(this, p, method, body, opts, m, r);
  }
}

export function mkErr(status: number, code: string, message: string) {
  const e: any = new Error(message);
  e.__api = { status, body: { error: { code, message, request_id: 'mock-' + Date.now() } } };
  return e;
}

let instance: MockBackend | null = null;
export function getMock(): MockBackend { return (instance ??= new MockBackend()); }
export function resetMock(): void { instance = null; }
